import React, { useState, useEffect, useRef } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { GetReportCard } from "../../api/ReportCards";
import { decodeValue, isFeatureEnabled } from "../global/GlobalTools";

const customStyles = {
  reportCardBase: {
    backgroundColor: "#ffffff",
    padding: "40px",
    marginBottom: "20px",
  },
  columnClasses: {
    width: "35%",
  },
  columnPeriods: {
    width: "65%",
    overflowX: "auto",
  },
  // CM-52583 (QA follow-up): single-scrollbar layout, applied ONLY in CF mode.
  // The classes column hides its own vertical scrollbar and mirrors the periods
  // column's scrollTop (see handleVerticalScrollSync) so both columns stay
  // row-aligned and only ONE vertical bar is visible. With the
  // `partial_averages_in_development_areas` FF off these are not spread in, so
  // the legacy layout (scroll owned by the wrapping Row) is preserved.
  columnClassesCF: {
    overflowY: "hidden",
  },
  columnPeriodsCF: {
    // overflowY is set explicitly (auto) instead of relying on the implicit
    // promotion from overflowX, to keep the intent clear.
    overflowY: "auto",
  },
  columnScore: {
    // width: "25%",
    // minWidth: "80px",
    border: "solid 1px #e9ecef",
    borderRight: "none",
    borderLeft: "none",
  },
  columnScoreRed: {
    backgroundColor: "#FFF0F3",
    border: "solid 1px #e9ecef",
    borderRight: "none",
    borderLeft: "none",
  },
  verticalText: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    textAlign: "center",
    borderRight: "solid 1px #e9ecef",
    borderTop: "solid 1px #e9ecef",
    borderBottom: "solid 1px #e9ecef",
    // whiteSpace: "nowrap",
    // display: "-webkit-box",
    // "-webkit-line-clamp": "3",
    // alignItems: "center",
  },
  fillCaracter: {
    background:
      "linear-gradient(to right, black 50%, rgba(255, 255, 255, 0) 0%)",
    backgroundSize: "10px 1px" /* Ajusta el tamaño del carácter repetido */,
    backgroundRepeat: "repeat-x",
    color: "transparent" /* Hace que el texto sea invisible */,
    lineHeight: 3 /* Ajusta la altura de línea si es necesario */,
  },
  // crear estilo para boxShadow cuando se haga hover
  shadowRowHover: {
    boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.5)",
    "&:hover": {
      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.5)",
    },
  },
};

const height = window.innerHeight;

const ReportCardComponent = (props) => {
  const [t] = useTranslation(["global", "reportCards"]);
  const { student_id, school_level_cycle } = useParams();
  const [periods, setPeriods] = useState([]);
  const [widthForPeriod, setWidthForPeriod] = useState(80);
  const [hasDevelopmentAreas, setHasDevelopmentAreas] = useState(false);
  const [reportCardInfo, setReportCardInfo] = useState();
  const [loading, setLoading] = useState(true);
  const [hasEvaluationCriteria, setHasEvaluationCriteria] = useState(false);
  const [dinamicHeightReport, setDinamicHeighReport] = useState({
    height: height - 400 + "px",
    overflowY: "auto",
  });
  const [showPartials, setShowPartials] = useState(true);
  // Controls the CF-level average columns (per-partial and period).
  const [showCFPartialAvg, setShowCFPartialAvg] = useState(false);
  // Controls the AVERAGE row showing period averages across CFs.
  const [showCFPeriodAvg, setShowCFPeriodAvg] = useState(false);
  // Controls the per-class weight "(XX%)" next to each CF class name.
  const [showCFWeights, setShowCFWeights] = useState(false);
  // CM-52583 (QA follow-up): the `partial_averages_in_development_areas` feature
  // flag, resolved once from the response handler. This is the ONE gate that
  // separates the CF flow from the legacy one: when it is false every renderer,
  // sub-header and style below behaves exactly as in master. Mirrors
  // `cfFeatureActive` in ReportCardBreakdownComponent.js.
  const [cfFeatureActive, setCFFeatureActive] = useState(false);
  // visible vertical scroll; the classes column mirrors its scrollTop.
  const classesColumnRef = useRef(null);
  const periodsColumnRef = useRef(null);
  // The classes column viewport is matched to the periods
  // column's real clientHeight (which is shortened by its horizontal scrollbar),
  // so both columns can scroll the same amount and rows stay aligned at the
  // bottom too. Defaults to the full height until measured.
  const [classesViewportHeight, setClassesViewportHeight] = useState(
    height - 400 + "px"
  );

  // Partials can be switched on while the cycle has no
  // partials created yet (every periods[].partials is empty). In that case the
  // CF renderers and the sub-header must fall back to the "period average
  // only" geometry ([per-class score | Pro.]); otherwise header and body
  // column widths drift apart. Only consumed inside the CF flow, which is
  // gated by the `partial_averages_in_development_areas` FF.
  const dataHasPartials = periods.some(
    (period) => (period.partials || []).length > 0
  );
  // CF view active. Used to (a) give the legacy AVERAGE and independent-class
  // cells an explicit width — they only set maxWidth, so they shrink to their
  // content and the whole row drifts out of column alignment — and (b) center
  // every value against the (sub)column it belongs to, mirroring the CF rows.
  // Also require hasDevelopmentAreas — the CF half-split
  // geometry must only apply when the report card is actually grouped by CF. With
  // CF flags on but a flat/ungrouped response (no development_area elements),
  // this stays false so plain class/average rows don't get phantom CF columns.
  // CM-52604 (QA punto 2/3 follow-up): mirrors the development_area dispatch
  // condition below — the per-period CF-average ("Pro.") column now renders
  // whenever classes are grouped by CF and we're not showing the per-class
  // partial breakdown without "Por Parciales" (that combination, tracked
  // separately, still has no Pro. column). This used to require
  // showCFPartialAvg/showCFPeriodAvg explicitly, which left this flag (and
  // every row that reads it — AVERAGE, class rows) out of sync once the
  // Pro. column started rendering unconditionally.
  // CM-52583 (QA follow-up): gated on `cfFeatureActive`. Two of the three
  // disjuncts below are independent of the CF feature flag, so without it this
  // turned itself on with the FF OFF (any report card grouped by CF that isn't
  // showing partials), leaking the CF half-split geometry and the info tooltip
  // into the legacy flow.
  const cfMode =
    cfFeatureActive &&
    hasDevelopmentAreas &&
    (showCFPartialAvg ||
      !showPartials ||
      !isFeatureEnabled("Mostrar_Parciales_en_Boletas"));

  // CM-52604 (QA punto 4): single source of truth for the per-period column
  // geometry when partials are shown, so the partials header and every body row
  // (class, CF, AVERAGE) compute identical widths and stop drifting
  // horizontally. Mirrors the reference classRowWithPartials formula exactly.
  const cfPeriodColumnWidth = (partialsCount) => {
    const numberPartials = partialsCount + 1;
    const surplus = partialsCount - 3;
    const widthAllPartialsPeriod = numberPartials * 42;
    return surplus > 0 && widthAllPartialsPeriod > widthForPeriod
      ? widthAllPartialsPeriod
      : widthForPeriod;
  };
  const cfPartialCellWidth = (partialsCount) =>
    cfPeriodColumnWidth(partialsCount) / (partialsCount + 1);

  useEffect(() => {
    getReportCard();
  }, []);

  // Monitor screen width with useEffect
  useEffect(() => {
    const observerResize = () => {
      handleResize();
      let dinamicHeight = { ...dinamicHeightReport };
      dinamicHeight.height = window.innerHeight - 400 + "px";
      setDinamicHeighReport(dinamicHeight);
    };
    handleResize();
    window.addEventListener("resize", observerResize);
    return () => {
      window.removeEventListener("resize", observerResize);
    };
  }, [periods, showCFPartialAvg, showCFPeriodAvg, hasDevelopmentAreas]);

  const handleResize = () => {
    const minWidth = 104;
    const widthScore = 100;
    const containerPeriods = document.getElementById("containerPeriods");
    if (!containerPeriods) {
      setTimeout(() => {
        handleResize();
      }, 500);
      return;
    }
    // Match the classes column viewport to the periods column's real inner
    // height (clientHeight already excludes its horizontal scrollbar), so both
    // columns scroll the same amount and rows stay aligned at the bottom.
    setClassesViewportHeight(containerPeriods.clientHeight + "px");
    const containerPeriodsWidth = containerPeriods.offsetWidth - widthScore;

    // CM-52583 (QA): general guard — with no periods created the division
    // below used to be `x / 0 = Infinity`, propagating Infinity/NaN px widths
    // through the whole layout. Applies to the legacy flow too (latent bug).
    const widthPeriod = periods.length
      ? containerPeriodsWidth / periods.length
      : minWidth;

    let newSize = widthPeriod > minWidth ? widthPeriod : minWidth;

    // CM-52583/CM-52586: in CF mode each partial splits into 2 sub-columns
    // (per-class score | CF average), so each period needs more width to avoid
    // overlap. Applied as a floor to `widthForPeriod` (shared by header and
    // every row); extra width handled by existing horizontal scroll.
    // Only widen for the CF sub-columns when the report card is
    // actually grouped by CF; otherwise a flat response with the CF flags on would
    // widen every plain class period cell for columns that never render.
    if ((showCFPartialAvg || showCFPeriodAvg) && hasDevelopmentAreas) {
      const maxPartials = periods.reduce(
        (max, p) => Math.max(max, (p.partials || []).length),
        0
      );
      const numberPartials = maxPartials + 1; // + "Pr." column
      const minCFWidth = numberPartials * 64; // ~32px per sub-column
      if (newSize < minCFWidth) newSize = minCFWidth;
    }

    setWidthForPeriod(newSize);
    return;
  };

  // Keep the classes column vertically in sync with the
  // periods column so both scroll as one and rows never drift apart. The periods
  // container is the only one showing a vertical scrollbar; scrolling it mirrors
  // its scrollTop onto the (scrollbar-hidden) classes column.
  const handleVerticalScrollSync = (event) => {
    if (classesColumnRef.current) {
      classesColumnRef.current.scrollTop = event.target.scrollTop;
    }
  };

  // Forward wheel gestures made over the classes column to the periods column
  // (the classes column hides its scrollbar, so it would not scroll on its own).
  const handleClassesWheel = (event) => {
    if (periodsColumnRef.current) {
      periodsColumnRef.current.scrollTop += event.deltaY;
    }
  };

  const getReportCard = () => {
    const studentId = decodeValue(student_id);
    const schoolLevelCycle = decodeValue(school_level_cycle);
    const payload = {
      student_id: studentId,
      organization_school_level_cycle_id: schoolLevelCycle,
    };
    GetReportCard(payload)
      .then((response) => {
        const reportCardResponse = response.data;
        setHasEvaluationCriteria(
          reportCardResponse.has_evaluation_criteria == 1
        );
        const showPartialsFeature = isFeatureEnabled(
          "Mostrar_Parciales_en_Boletas"
        );
        setShowPartials(
          reportCardResponse.show_partial_results == 1 && showPartialsFeature
        );
        // CM-52583: the CF-averages feature flag, resolved once and shared by
        // every switch below (they all require it) and by `cfFeatureActive`,
        // the single gate that keeps the legacy flow byte-identical to master.
        const cfAveragesFeature = isFeatureEnabled(
          "partial_averages_in_development_areas"
        );
        setCFFeatureActive(cfAveragesFeature);
        // CM-52583: flag that controls the visibility of the CF-level average
        // columns (same as the template). Comes from the endpoint at the same
        // level as `show_partial_results` (value 0 or 1).
        const showCFPartialAvgResolved =
          reportCardResponse.show_cf_partial_avg == 1 && cfAveragesFeature;
        setShowCFPartialAvg(showCFPartialAvgResolved);
        // CM-52586: flag that controls the cross-CF AVERAGE row.
        const showCFPeriodAvgResolved =
          reportCardResponse.show_cf_period_avg == 1 && cfAveragesFeature;
        setShowCFPeriodAvg(showCFPeriodAvgResolved);
        // CM-52589: controls the per-class weight "(XX%)" shown next to each CF
        // class name. Comes from the endpoint flag `show_cf_weights` (the "Class
        // Weights" switch), combined with the FF.
        const showCFWeightsResolved =
          reportCardResponse.show_cf_weights == 1 && cfAveragesFeature;
        setShowCFWeights(showCFWeightsResolved);
        if (response.status) {
          reportCardResponse.elements.forEach((element) => {
            if (element.type === "class") {
              if (element.belongs_class === 0) {
                element.remove = true;
                return;
              }
            } else if (element.type === "development_area") {
              element.classes.forEach((classElement) => {
                if (classElement.belongs_class === 0) {
                  classElement.remove = true;
                  return;
                }
              });
            }
          });
          // remover las clases de los elements y dentro de los development_area que tengan remove
          reportCardResponse.elements.forEach((element) => {
            if (element.type === "development_area") {
              element.classes = element.classes.filter(
                (classElement) => !classElement.remove
              );
            }
          });
          reportCardResponse.elements = reportCardResponse.elements.filter(
            (element) => !element.remove
          );

          const classElement = reportCardResponse.elements.find(
            (element) => element.type === "class"
          );
          // Get the peridos of theElement class
          let periodsClass = [];
          // Search for an element of type development area
          const developmentAreaElement = reportCardResponse.elements.find(
            (element) => element.type === "development_area"
          );
          if (developmentAreaElement) setHasDevelopmentAreas(true);

          if (classElement) {
            periodsClass = classElement.periods;
          } else {
            // Get the peridos of theElement class
            if (developmentAreaElement) {
              periodsClass = developmentAreaElement.classes[0].periods;
            }
          }

          setReportCardInfo(reportCardResponse);
          props.setStudentName(reportCardResponse.student_name);
          // añadir a cada periodo un array de parciales
          // periodsClass.forEach((period) => {
          //   period.partials = partialsDummy;
          // });
          setPeriods(periodsClass);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const headerPeriods = () => {
    return (
      <Row style={{ width: "max-content" }}>
        <div
          className="px-0"
          style={{
            minWidth: widthForPeriod * periods.length + "px",
            width: "fit-content",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "18px",
            }}
          >
            {periods.map((period, index) => {
              const partials = period.partials || [];
              const totalPartials = partials.length + 1;
              const widthAllPartials = totalPartials * 42;
              const surplus = partials.length - 3;
              let columnWidth = widthForPeriod;
              if (
                surplus > 0 &&
                showPartials &&
                widthAllPartials > widthForPeriod * periods.length
              ) {
                // columnWidth = widthForPeriod + surplus * 42;
                columnWidth = widthAllPartials;
              }
              return (
                <div
                  key={index}
                  style={{
                    width: columnWidth + "px",
                    textAlign: showPartials ? "center" : "",
                    // CM-52604 (QA punto 4): "block ruby" inlinifies its direct
                    // children, which are then positioned by baseline within the
                    // ruby line instead of flush with the container's top edge —
                    // pinning to the top neutralizes that quirk (see the other
                    // period-column divs in this file for the same fix).
                    // CM-52583 (QA follow-up): CF mode only — the legacy header
                    // must keep master's baseline alignment.
                    ...(cfFeatureActive ? { verticalAlign: "top" } : {}),
                  }}
                >
                  <b>
                    {t("reportCards:reportCardBasic.period") +
                      " " +
                      (index + 1)}
                  </b>
                </div>
              );
            })}
          </Row>
        </div>
        <div style={{ width: "100px" }}>
          <b>{t("reportCards:reportCardBasic.average")}</b>
        </div>
      </Row>
    );
  };

  /**
   * Legacy partials sub-header — kept byte-identical to master so the report card
   * renders exactly as in production when the
   * `partial_averages_in_development_areas` FF is off. The CF geometry lives in
   * cfPartialsHeader() below.
   */
  const partialsHeader = () => {
    return (
      <Row
        className="px-0 mx-0"
        style={{
          display: "block ruby",
          height: "18px",
          width: "max-content",
        }}
      >
        {periods.map((period, index) => {
          const partials = period.partials || [];
          const numberPartials = partials.length + 1;
          const surplus = numberPartials - 3;
          const widthAllPartials = numberPartials * 42;
          let columnWidth = widthForPeriod;
          if (
            surplus > 0 &&
            widthAllPartials > widthForPeriod * periods.length
          ) {
            columnWidth = widthForPeriod + surplus * 42;
          }
          let widthParcial = columnWidth / numberPartials;
          if (columnWidth <= widthForPeriod) {
            widthParcial = columnWidth / numberPartials;
          }
          return (
            <div
              key={index}
              style={{
                maxWidth: columnWidth + "px",
                width: columnWidth + "px",
              }}
              className="ps-0 mx-0"
            >
              <Row>
                {partials.map((item, periodIndex) => {
                  return (
                    <div
                      key={periodIndex}
                      style={{ width: widthParcial + "px" }}
                    >
                      {/* check whether this is the last partial, to add a margin */}
                      <b>{periodIndex + 1}</b>
                    </div>
                  );
                })}
                <div style={{ width: widthParcial + "px" }}>
                  {/* check whether this is the last partial, to add a margin */}
                  <b>{t("reportCards:page.abridgedAverage")}</b>
                </div>
              </Row>
            </div>
          );
        })}
      </Row>
    );
  };

  /**
   * Partials sub-header for the CF flow (CM-52583/CM-52604). Only reachable with
   * the `partial_averages_in_development_areas` FF active.
   */
  const cfPartialsHeader = () => {
    return (
      <Row
        className="px-0 mx-0"
        style={{
          display: "block ruby",
          height: "18px",
          width: "max-content",
        }}
      >
        {periods.map((period, index) => {
          const partials = period.partials || [];
          // CM-52604 (QA punto 4): shared per-period geometry (see helper) so the
          // header lines up exactly with the class/CF/AVERAGE body rows.
          const columnWidth = cfPeriodColumnWidth(partials.length);
          const widthParcial = cfPartialCellWidth(partials.length);
          return (
            <div
              key={index}
              style={{
                maxWidth: columnWidth + "px",
                width: columnWidth + "px",
                // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                // alignment of inlinified children (see headerPeriods).
                verticalAlign: "top",
                // CM-52604 (QA punto 6): Bootstrap's ".row > *" rule injects
                // ~12px of gutter padding on EACH side of any direct child of
                // a ".row", regardless of the row's own "px-0" — that silently
                // eats into this column's declared width, forcing the browser
                // to flex-shrink its sub-columns to fit and drift out of
                // alignment with the body rows below. "px-0" (both sides, not
                // just "ps-0") removes it so the full declared width is real.
              }}
              className="px-0 mx-0"
            >
              {/* CM-52604 (QA punto 2 follow-up): force a single flex line.
                  Bootstrap's default flex-wrap:wrap makes this row wrap its
                  last child (Pro.) back to the start whenever the sub-columns'
                  combined width is a sub-pixel hair over the container's
                  rounded width — an intermittent rounding artifact, not a
                  real overflow. */}
              <Row className="mx-0" style={{ flexWrap: "nowrap" }}>
                {partials.map((item, periodIndex) => {
                  return (
                    <div
                      key={periodIndex}
                      style={{ width: widthParcial + "px" }}
                    >
                      {/* check whether this is the last partial, to add a margin */}
                      <b>{periodIndex + 1}</b>
                    </div>
                  );
                })}
                <div style={{ width: widthParcial + "px" }}>
                  {/* Only label the period-average
                      ("Pro.") sub-column when the period actually has partials.
                      A period without partials shows just its own value, so the
                      "Pro." label is redundant and confusing. */}
                  {partials.length > 0 && (
                    <b>{t("reportCards:page.abridgedAverage")}</b>
                  )}
                </div>
              </Row>
            </div>
          );
        })}
      </Row>
    );
  };

  // Sub-header for the CF view when the cycle has
  // periods but no partials created (dataHasPartials = false). Mirrors the
  // geometry of developmentAreaRowWithCFAverage / averageRowWithCFPeriodAvgOnly:
  // each period cell is split in two halves — left (per-class score) empty,
  // right showing the "Pro." label. Only reachable with the
  // `partial_averages_in_development_areas` FF active (showCFPartialAvg).
  const cfAverageSubHeader = () => {
    return (
      <Row
        className="px-0 mx-0"
        style={{
          display: "block ruby",
          height: "18px",
          width: "max-content",
        }}
      >
        {periods.map((period, index) => {
          return (
            <div
              key={index}
              style={{
                maxWidth: widthForPeriod + "px",
                width: widthForPeriod + "px",
                display: "flex",
                // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                // alignment of inlinified flex children (see headerPeriods).
                verticalAlign: "top",
              }}
              className="ps-0 mx-0"
            >
              {/* Left half: empty (aligns with the per-class score column) */}
              <div style={{ width: widthForPeriod / 2 + "px" }} />
              {/* Right half: CF period average ("Pro.") label */}
              <div
                style={{
                  width: widthForPeriod / 2 + "px",
                  textAlign: "center",
                }}
              >
                <b>{t("reportCards:page.abridgedAverage")}</b>
              </div>
            </div>
          );
        })}
      </Row>
    );
  };

  const averageRow = (element, index, redRow) => {
    return (
      <Row key={index} style={{ width: "max-content" }}>
        <div
          className="px-0"
          style={{
            height: "18px",
            width: widthForPeriod * periods.length + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "18px",
            }}
          >
            {element.periods.map((period, index) => {
              // CM-52604 (QA punto 2/3 follow-up): this row runs whenever
              // showPartials is off — which, now that the per-period CF
              // average column renders whenever classes are grouped (see
              // cfMode), means it must mirror developmentAreaRowWithCFAverage's
              // own split: [empty left half | value centered in the right
              // half] — the same slot the group rows' "Pro." value occupies —
              // instead of centering across the whole period (which used to be
              // correct back when this row could never coexist with a visible
              // Pro. column).
              if (cfMode) {
                return (
                  <div
                    key={index}
                    style={{
                      ...redRow,
                      maxWidth: widthForPeriod + "px",
                      width: widthForPeriod + "px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
                      // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                      // alignment of inlinified flex children (see headerPeriods).
                      verticalAlign: "top",
                    }}
                  >
                    <div style={{ width: widthForPeriod / 2 + "px" }} />
                    <div
                      style={{
                        width: widthForPeriod / 2 + "px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <b>{period.score}</b>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={index}
                  style={{
                    ...redRow,
                    maxWidth: widthForPeriod + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
                    // CM-52583 (QA follow-up): CF mode only — this branch is the
                    // legacy one and must keep master's baseline alignment.
                    ...(cfFeatureActive ? { verticalAlign: "top" } : {}),
                  }}
                >
                  <b>{period.score}</b>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            width: "100px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
          }}
        >
          <b>{element.total_average}</b>
        </div>
      </Row>
    );
  };

  /**
   * Legacy AVERAGE row with partials — kept byte-identical to master for the
   * FF-off flow. The CF variant is cfAverageRowWithPartials() below.
   */
  const averageRowWhithPartials = (element, index, redRow) => {
    const totalPartials = element.periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length;
    }, 0);
    const countSurplus = totalPartials - element.periods.length * 3;
    let rowWidth = widthForPeriod * periods.length;
    if (countSurplus > 0 && showPartials) {
      rowWidth = rowWidth + countSurplus * 42;
    }
    return (
      <Row key={index} style={{ width: "max-content" }}>
        <div
          className="px-0"
          style={{
            height: "18px",
            minWidth: rowWidth + "px",
            width: "max-content",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "18px",
            }}
          >
            {element.periods.map((period, index) => {
              const partials = period.partials || [];
              const surplus = partials.length - 3;
              let columnWidth = widthForPeriod;
              if (surplus > 0 && showPartials) {
                columnWidth = widthForPeriod + surplus * 42;
              }
              return (
                <div
                  key={index}
                  style={{
                    ...redRow,
                    maxWidth: columnWidth + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
                    borderRight: "solid 1px #e9ecef",
                    paddingRight: "30px",
                  }}
                >
                  <b>{period.score}</b>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            width: "100px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
          }}
        >
          <b>{element.total_average}</b>
        </div>
      </Row>
    );
  };

  /**
   * AVERAGE row with partials for the CF flow (CM-52583/CM-52604). Only
   * reachable with the `partial_averages_in_development_areas` FF active.
   */
  const cfAverageRowWithPartials = (element, index, redRow) => {
    // CM-52604 (QA punto 4): homologate the base row width with
    // classRowWithPartials (Σ(partials+1)*42) so the overall average row keeps
    // the same total width as the class rows once partials exist and stops
    // drifting horizontally. The "average" element's own periods only carry
    // `.score` (no partials array), so the partial counts come from the shared
    // `periods` state — the same source partialsHeader()/the class rows use.
    const totalPartials = periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    let rowWidth = widthForPeriod * periods.length;
    if (showPartials && widthAllPartials > widthForPeriod * periods.length) {
      rowWidth = widthAllPartials;
    }
    return (
      <Row key={index} style={{ width: "max-content" }}>
        <div
          className="px-0"
          style={{
            height: "18px",
            minWidth: rowWidth + "px",
            width: "max-content",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "18px",
            }}
          >
            {element.periods.map((period, index) => {
              // CM-52583 (Pro. alignment): the "average" element's own
              // periods only carry `.score` (confirmed against the real
              // endpoint response — unlike `development_area_average`,
              // they never carry a `partials`/`partials_average` array), so
              // the partial count for this period must come from the
              // shared `periods` state instead — the same source
              // `partialsHeader()` uses to build the header split.
              const partials = (periods[index] && periods[index].partials) || [];
              // CM-52604 (QA punto 4): shared per-period geometry (helper) so
              // this row lines up with the header and the other rows.
              const columnWidth = cfPeriodColumnWidth(partials.length);
              // In CF mode this row must not center its value across the
              // whole period (which now spans the partial sub-columns too)
              // — it belongs only under "Pro.". Mirrors the same condition
              // used to pick between averageRowWithCFPeriodAvgAndPartials/
              // Only (line ~1995).
              if (cfMode) {
                if (showCFPartialAvg && dataHasPartials) {
                  // CM-52604 (QA punto 4): shared per-period geometry so the
                  // "Pro." sub-column lands pixel-aligned with the CF rows'
                  // "Pro." values above it and with the header.
                  const cfColumnWidth = cfPeriodColumnWidth(partials.length);
                  const widthParcial = cfPartialCellWidth(partials.length);
                  return (
                    <div
                      key={index}
                      style={{
                        ...redRow,
                        maxWidth: cfColumnWidth + "px",
                        width: cfColumnWidth + "px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
                        borderRight: "solid 1px #e9ecef",
                        // CM-52604 (QA punto 4): neutralize "block ruby"'s
                        // baseline alignment of inlinified flex children.
                        verticalAlign: "top",
                        // CM-52604 (QA punto 6): Bootstrap's ".row > *" rule
                        // injects ~12px gutter padding per side on any direct
                        // child of a ".row" — silently shrinks this column's
                        // real content width and drifts its "Pro." value away
                        // from the CF rows' own "Pro." above it.
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                    >
                      {partials.map((_, partialIndex) => (
                        <div
                          key={partialIndex}
                          style={{ width: widthParcial + "px", height: "40px" }}
                        />
                      ))}
                      <div
                        style={{
                          width: widthParcial + "px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <b>{period.score}</b>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={index}
                    style={{
                      ...redRow,
                      maxWidth: columnWidth + "px",
                      width: columnWidth + "px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
                      borderRight: "solid 1px #e9ecef",
                      // CM-52604 (QA punto 4): neutralize "block ruby"'s
                      // baseline alignment of inlinified flex children.
                      verticalAlign: "top",
                      // CM-52604 (QA punto 6): see the sibling branch above —
                      // Bootstrap's ".row > *" gutter padding leak.
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                  >
                    <div
                      style={{ width: columnWidth / 2 + "px", height: "40px" }}
                    />
                    <div
                      style={{
                        width: columnWidth / 2 + "px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <b>{period.score}</b>
                    </div>
                  </div>
                );
              }
              // CM-52604 (QA punto 4): shared per-period geometry (helper) — the
              // period average sits under the last ("Pro.") sub-column so it
              // lines up with the class rows' period-average column.
              const avgColumnWidth = cfPeriodColumnWidth(partials.length);
              const avgWidthParcial = cfPartialCellWidth(partials.length);
              return (
                <div
                  key={index}
                  style={{
                    ...redRow,
                    maxWidth: avgColumnWidth + "px",
                    width: avgColumnWidth + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
                    borderRight: "solid 1px #e9ecef",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                    // alignment of inlinified flex children.
                    verticalAlign: "top",
                    // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                    // padding leak — see the cfMode branches above.
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {/* CM-52604 (QA punto 2): bare <Row> (keeps Bootstrap's negative
                      gutter, ~12px) so this row's sub-columns land pixel-aligned
                      with classRowWithPartials/partialsHeader.
                      flexWrap:"nowrap" (QA punto 2 follow-up): without it, Bootstrap's
                      default flex-wrap:wrap intermittently wraps this row's last
                      (Pro.) child back to the start whenever sub-pixel rounding makes
                      the sub-columns' combined width a hair over the container.
                      CM-52604 (QA punto 5): center the value in the last ("Pro.")
                      sub-column — this is the CF-level "Pro." slot, which is always
                      centered (developmentAreaRowWithCFPartials, averageRowWithCFPeriodAvgOnly);
                      was left-aligned, drifting away from those under it. */}
                  <Row className="mx-0" style={{ flexWrap: "nowrap" }}>
                    {partials.map((_, partialIndex) => (
                      <div
                        key={partialIndex}
                        style={{ width: avgWidthParcial + "px" }}
                      />
                    ))}
                    <div
                      style={{
                        width: avgWidthParcial + "px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <b>{period.score}</b>
                    </div>
                  </Row>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            width: "100px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
          }}
        >
          <b>{element.total_average}</b>
        </div>
      </Row>
    );
  };

  // CM-52586: `development_area_average` row when show_cf_partial_avg=1 and
  // show_cf_period_avg=1. Mirrors developmentAreaRowWithCFPartials geometry:
  // per period the left partial sub-column is empty (aligns with the per-class
  // score column above) and the right sub-column shows the across-CF partial
  // average; the "Pr." column shows the across-CF period average. All cells use
  // the #FFF0F3 highlight. Column widths are derived from partials_average[]
  // (not from period.partials, which is absent on this element type).
  const averageRowWithCFPeriodAvgAndPartials = (element, index) => {
    const totalPartials = element.periods.reduce((acc, period) => {
      const cfAvg = period.partials_average || [];
      return acc + cfAvg.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    const countSurplus = totalPartials - element.periods.length * 3;
    let rowWidth = widthForPeriod * periods.length;
    if (
      countSurplus > 0 &&
      showPartials &&
      widthAllPartials > widthForPeriod * periods.length
    ) {
      rowWidth = widthAllPartials;
    }
    return (
      <Row key={index} style={{ width: "max-content" }}>
        <div
          className="px-0"
          style={{
            height: "40px",
            width: rowWidth + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "40px",
            }}
          >
            {element.periods.map((period, p_index) => {
              const cfPartialsAvg = period.partials_average || [];
              // CM-52604 (QA punto 4): shared per-period geometry so the cross-CF
              // AVERAGE row lines up exactly with the header and the CF rows.
              const columnWidth = cfPeriodColumnWidth(cfPartialsAvg.length);
              const widthParcial = cfPartialCellWidth(cfPartialsAvg.length);
              return (
                <div
                  key={p_index}
                  style={{
                    maxWidth: columnWidth + "px",
                    // CM-52583 (QA follow-up): explicit width so the cell
                    // doesn't shrink to its content and every row shares the
                    // exact same period column width.
                    width: columnWidth + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    borderRight: "solid 1px #e9ecef",
                    backgroundColor: "#FFF0F3",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                    // alignment of inlinified flex children.
                    verticalAlign: "top",
                    // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                    // padding leak (~12px/side on any direct child of a
                    // ".row") — without this the sub-columns get squeezed by
                    // flex-shrink and drift out of alignment.
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {cfPartialsAvg.map((_, partialIndex) => (
                    <div
                      key={partialIndex}
                      style={{
                        width: widthParcial + "px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        borderRight: "solid 1px #e9ecef",
                      }}
                    >
                      {/* Left sub-column: empty (aligns with per-class score above) */}
                      <div
                        style={{
                          width: widthParcial / 2 + "px",
                          borderRight: "solid 1px #e9ecef",
                          height: "40px",
                        }}
                      />
                      {/* Right sub-column: across-CF partial average */}
                      <div
                        style={{
                          width: widthParcial / 2 + "px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <b>
                          {(cfPartialsAvg[partialIndex] &&
                            cfPartialsAvg[partialIndex].average) ||
                            "-"}
                        </b>
                      </div>
                    </div>
                  ))}
                  {/* "Pr." column: across-CF period average */}
                  <div
                    style={{
                      width: widthParcial + "px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <b>{period.average || "-"}</b>
                  </div>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            width: "100px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            backgroundColor: "#FFF0F3",
          }}
        >
          <b>{element.total_average}</b>
        </div>
      </Row>
    );
  };

  // CM-52586: `development_area_average` row when show_cf_partial_avg=0 and
  // show_cf_period_avg=1. No partial sub-columns; each period cell is split
  // in half — left half empty (aligns with CF rows above), right half shows
  // the across-CF period average ("Pr."). All cells use the #FFF0F3 highlight.
  // CM-52604 (QA punto 5): when partials are visible without "Por Parciales"
  // (cfMode false), the CF rows above switch to the wider partials-aware
  // geometry (developmentAreaRowWithPartials) — mirror that geometry here too
  // (n empty partial-width slots + 1 value slot) instead of the widthForPeriod
  // 50/50 split, otherwise this row's total width and per-period offsets stay
  // narrower than the rows above and the values drift out of alignment.
  const averageRowWithCFPeriodAvgOnly = (element, index) => {
    const totalPartials = periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    let rowWidth = widthForPeriod * periods.length;
    if (!cfMode && widthAllPartials > widthForPeriod * periods.length) {
      rowWidth = widthAllPartials;
    }
    return (
      <Row key={index} style={{ width: "max-content" }}>
        <div
          className="px-0"
          style={{
            height: "40px",
            width: rowWidth + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "40px",
            }}
          >
            {element.periods.map((period, p_index) => {
              if (cfMode) {
                // CM-52604 (QA punto 3): split each period cell in half so the
                // value aligns under the "Pro." sub-column of the CF group
                // rows above (same pattern as averageRow's cfMode branch).
                return (
                  <div
                    key={p_index}
                    style={{
                      maxWidth: widthForPeriod + "px",
                      width: widthForPeriod + "px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      borderRight: "solid 1px #e9ecef",
                      backgroundColor: "#FFF0F3",
                      // CM-52604 (QA punto 4): neutralize "block ruby"'s
                      // baseline alignment of inlinified flex children.
                      verticalAlign: "top",
                      // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                      // padding leak.
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                  >
                    <div style={{ width: widthForPeriod / 2 + "px" }} />
                    <div
                      style={{
                        width: widthForPeriod / 2 + "px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <b>{period.average || "-"}</b>
                    </div>
                  </div>
                );
              }
              const partials = (periods[p_index] && periods[p_index].partials) || [];
              const columnWidth = cfPeriodColumnWidth(partials.length);
              const widthParcial = cfPartialCellWidth(partials.length);
              return (
                <div
                  key={p_index}
                  style={{
                    maxWidth: columnWidth + "px",
                    width: columnWidth + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    borderRight: "solid 1px #e9ecef",
                    backgroundColor: "#FFF0F3",
                    verticalAlign: "top",
                    // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                    // padding leak.
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {/* CM-52604 (QA punto 5): match classRowWithPartials/
                      developmentAreaRowWithPartials exactly — bare <Row>
                      (keeps Bootstrap's negative gutter, ~12px) so this row's
                      partial columns land pixel-aligned with theirs. */}
                  <Row className="mx-0" style={{ flexWrap: "nowrap" }}>
                    {partials.map((_, partialIndex) => (
                      <div
                        key={partialIndex}
                        style={{ width: widthParcial + "px", height: "40px" }}
                      />
                    ))}
                    <div
                      style={{
                        width: widthParcial + "px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <b>{period.average || "-"}</b>
                    </div>
                  </Row>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            width: "100px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            backgroundColor: "#FFF0F3",
          }}
        >
          <b>{element.total_average}</b>
        </div>
      </Row>
    );
  };

  const classRow = (element, index, redRow, space) => {
    return (
      <Row
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "innerShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: "40px",
            width: space + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "40px",
            }}
          >
            {element.periods.map((period, index) => {
              return (
                <div
                  key={index}
                  style={{
                    ...redRow,
                    maxWidth: widthForPeriod + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                    // alignment of inlinified flex children. CM-52583 (QA
                    // follow-up): CF mode only, so the legacy row keeps
                    // master's baseline alignment.
                    ...(cfFeatureActive ? { verticalAlign: "top" } : {}),
                    // CM-52604 (QA punto 2): this row only renders when
                    // showPartials is off (so showCFPartialAvg is off too — no
                    // per-period CF-avg column). With CF grouping active, center
                    // the independent-class score across the full period so it
                    // aligns with the centered AVERAGE rows.
                    ...(cfMode
                      ? {
                          width: widthForPeriod + "px",
                          justifyContent: "center",
                        }
                      : {}),
                  }}
                >
                  {period.score}
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            ...redRow,
            width: "100px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
          }}
        >
          {element.average}
        </div>
      </Row>
    );
  };

  /**
   * Legacy class row with partials — kept byte-identical to master for the
   * FF-off flow. The CF variant is cfClassRowWithPartials() below.
   */
  const classRowWithPartials = (element, index, redRow, space) => {
    const totalPartials = element.periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    // const countSurplus = totalPartials - element.periods.length * 3;

    if (showPartials && widthAllPartials > widthForPeriod * periods.length) {
      // space = space + countSurplus * 42;
      space = widthAllPartials;
    }
    return (
      <Row
        id={"rowClass-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "innerShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: "40px",
            minWidth: space + "px",
            width: "fit-content",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "40px",
            }}
          >
            {element.periods.map((period, index) => {
              const partials = period.partials || [];
              const numberPartials = partials.length + 1;
              const surplus = partials.length - 3;
              const widthAllPartialsPeriod = numberPartials * 42;
              let columnWidth = widthForPeriod;
              if (surplus > 0 && widthAllPartialsPeriod > widthForPeriod) {
                // columnWidth = widthForPeriod + surplus * 42;
                columnWidth = widthAllPartialsPeriod;
              }
              let widthParcial = columnWidth / numberPartials;
              if (columnWidth <= widthForPeriod) {
                widthParcial = columnWidth / numberPartials;
              }
              return (
                <div
                  key={index}
                  style={{
                    ...redRow,
                    maxWidth: columnWidth + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",

                    paddingRight: 0,
                    borderRight: "solid 1px #e9ecef",
                  }}
                >
                  <Row>
                    {partials.map((item, periodIndex) => {
                      return (
                        <div
                          key={periodIndex}
                          style={{ width: widthParcial + "px" }}
                        >
                          {/* check whether this is the last partial, to add a margin */}
                          {item.score || "-"}
                        </div>
                      );
                    })}
                    <div style={{ width: widthParcial + "px" }}>
                      {/* check whether this is the last partial, to add a margin */}
                      {<b>{period.score || "-"}</b>}
                    </div>
                  </Row>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            ...redRow,
            width: "99px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
          }}
        >
          {<b>{element.average}</b> || "-"}
        </div>
      </Row>
    );
  };

  /**
   * Class row with partials for the CF flow (CM-52583/CM-52604). Only reachable
   * with the `partial_averages_in_development_areas` FF active.
   */
  const cfClassRowWithPartials = (element, index, redRow, space) => {
    const totalPartials = element.periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    // const countSurplus = totalPartials - element.periods.length * 3;

    if (showPartials && widthAllPartials > widthForPeriod * periods.length) {
      // space = space + countSurplus * 42;
      space = widthAllPartials;
    }
    return (
      <Row
        id={"rowClass-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "innerShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: "40px",
            minWidth: space + "px",
            width: "fit-content",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: "40px",
            }}
          >
            {element.periods.map((period, index) => {
              const partials = period.partials || [];
              // CM-52604 (QA punto 4): shared per-period geometry (helper), the
              // reference the header and every other row now match.
              const columnWidth = cfPeriodColumnWidth(partials.length);
              const widthParcial = cfPartialCellWidth(partials.length);
              return (
                <div
                  key={index}
                  style={{
                    ...redRow,
                    maxWidth: columnWidth + "px",
                    width: columnWidth + "px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",

                    // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                    // padding leak (~12px/side on any direct child of a
                    // ".row") — paddingRight:0 alone wasn't enough, the left
                    // side leaked too.
                    paddingLeft: 0,
                    paddingRight: 0,
                    borderRight: "solid 1px #e9ecef",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                    // alignment of inlinified flex children.
                    verticalAlign: "top",
                  }}
                >
                  {/* CM-52604 (QA punto 2 follow-up): force a single flex line —
                      see partialsHeader for why flex-wrap:wrap silently misplaces
                      the last ("Pro.") sub-column here. */}
                  <Row className="mx-0" style={{ flexWrap: "nowrap" }}>
                    {partials.map((item, periodIndex) => {
                      return (
                        <div
                          key={periodIndex}
                          style={{
                            width: widthParcial + "px",
                            // CM-52583 (QA follow-up): in CF mode the CF rows
                            // split each partial in [class score | CF avg]
                            // halves; center the independent-class score
                            // against the left (class score) half so both
                            // line up vertically.
                            ...(cfMode
                              ? {
                                  textAlign: "center",
                                  paddingRight: widthParcial / 2 + "px",
                                }
                              : {}),
                          }}
                        >
                          {/* check whether this is the last partial, to add a margin */}
                          {item.score || "-"}
                        </div>
                      );
                    })}
                    <div
                      style={{
                        width: widthParcial + "px",
                        // CM-52604 (QA punto 5): always center this last
                        // ("Pro.") sub-column — it's the same slot the CF
                        // rows center their own merged average in
                        // (developmentAreaRowWithPartials et al.), whether or
                        // not this particular class belongs to a CF group.
                        textAlign: "center",
                      }}
                    >
                      {/* check whether this is the last partial, to add a margin */}
                      {<b>{period.score || "-"}</b>}
                    </div>
                  </Row>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            ...redRow,
            width: "99px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
          }}
        >
          {<b>{element.average}</b> || "-"}
        </div>
      </Row>
    );
  };

  /**
   * Legacy development-area row (no partials) — kept byte-identical to master
   * for the FF-off flow. CM-52583 (QA follow-up): this renderer was dropped when
   * the CF flow was built, which routed the legacy "CF grouping + partials off"
   * case to developmentAreaRowWithCFAverage and painted a phantom half-column of
   * "-" where master shows only the per-class period score.
   */
  const developmentAreaRow = (
    element,
    index,
    redRow,
    space,
    numberOfClasses
  ) => {
    return (
      <Row
        id={"rowDevelopmentArea-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "externalShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: 40 * numberOfClasses + "px",
            width: space + "px",
          }}
        >
          {element.classes.map((singleClass, c_index) => {
            return (
              <Row
                className="px-0 mx-0"
                key={c_index}
                style={{
                  display: "block ruby",
                  height: "40px",
                }}
              >
                {singleClass.periods.map((period, p_index) => {
                  return (
                    <div
                      key={p_index}
                      style={{
                        ...redRow,
                        maxWidth: widthForPeriod + "px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {period.score}
                    </div>
                  );
                })}
              </Row>
            );
          })}
        </div>
        <div
          style={{
            ...redRow,
            width: "100px",
            height: 40 * numberOfClasses + "px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            // backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
          }}
        >
          {element.average}
        </div>
      </Row>
    );
  };

  /**
   * Legacy development-area row with partials — kept byte-identical to master
   * for the FF-off flow. Each period's last ("Pro.") sub-column shows THAT
   * class's own period average (`period.score`), one value per class row; it
   * must not read `element.periods[]`, which only exists in the CF flow.
   * The CF variant is developmentAreaRowWithCFAvgAndPartials() below.
   */
  const developmentAreaRowWithPartials = (
    element,
    index,
    redRow,
    space,
    numberOfClasses
  ) => {
    const totalPartials = element.classes[0].periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    const countSurplus = totalPartials - element.classes[0].periods.length * 3;
    if (
      countSurplus > 0 &&
      showPartials &&
      widthAllPartials > widthForPeriod * periods.length
    ) {
      // space = space + countSurplus * 42;
      space = widthAllPartials;
    }

    return (
      <Row
        id={"rowDevelopmentArea-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "externalShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: 40 * numberOfClasses + "px",
            width: space + "px",
          }}
        >
          {element.classes.map((singleClass, c_index) => {
            return (
              <Row
                className="px-0 mx-0"
                key={c_index}
                style={{
                  display: "block ruby",
                  height: "40px",
                }}
              >
                {singleClass.periods.map((period, p_index) => {
                  const partials = period.partials || [];
                  const numberPartials = partials.length + 1;
                  const surplus = partials.length - 3;
                  const widthAllPartials = numberPartials * 42;
                  let columnWidth = widthForPeriod;
                  if (surplus > 0 && widthAllPartials > widthForPeriod) {
                    columnWidth = widthForPeriod + surplus * 42;
                  }
                  let widthParcial = columnWidth / numberPartials;
                  if (columnWidth <= widthForPeriod) {
                    widthParcial = (columnWidth - 2) / numberPartials;
                  }
                  return (
                    <div
                      key={p_index}
                      style={{
                        ...redRow,
                        maxWidth: columnWidth + "px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        borderRight: "solid 1px #e9ecef",
                      }}
                    >
                      <Row>
                        {partials.map((item, periodIndex) => {
                          return (
                            <div
                              key={periodIndex}
                              style={{ width: widthParcial + "px" }}
                            >
                              {/* check whether this is the last partial, to add a margin */}
                              {item.score || "-"}
                            </div>
                          );
                        })}
                        <div style={{ width: widthParcial + "px" }}>
                          {/* check whether this is the last partial, to add a margin */}
                          {<b>{period.score || "-"}</b>}
                        </div>
                      </Row>
                    </div>
                  );
                })}
              </Row>
            );
          })}
        </div>
        <div
          style={{
            ...redRow,
            width: "100px",
            height: 40 * numberOfClasses + "px",
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            // backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
          }}
        >
          <b>{element.average}</b>
        </div>
      </Row>
    );
  };

  /**
   * Development-area row with partials for the CF flow (CM-52604 punto 5): the
   * "Pro." sub-column is merged at CF-group level and shows
   * `element.periods[].average` instead of each class's own period average.
   * Only reachable with the `partial_averages_in_development_areas` FF active.
   */
  const developmentAreaRowWithCFAvgAndPartials = (
    element,
    index,
    redRow,
    space,
    numberOfClasses
  ) => {
    const cfPeriods = element.periods || [];
    const totalPartials = element.classes[0].periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    const countSurplus = totalPartials - element.classes[0].periods.length * 3;
    if (
      countSurplus > 0 &&
      showPartials &&
      widthAllPartials > widthForPeriod * periods.length
    ) {
      // space = space + countSurplus * 42;
      space = widthAllPartials;
    }
    const blockHeight = 40 * numberOfClasses + "px";

    return (
      <Row
        id={"rowDevelopmentArea-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "externalShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: blockHeight,
            width: space + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: blockHeight,
            }}
          >
            {element.classes[0].periods.map((period, p_index) => {
              const partials = period.partials || [];
              // CM-52604 (QA punto 4): shared per-period geometry so this
              // grouped row lines up with the header and the other rows.
              const columnWidth = cfPeriodColumnWidth(partials.length);
              const widthParcial = cfPartialCellWidth(partials.length);
              const cfPeriod = cfPeriods[p_index] || {};
              return (
                <div
                  key={p_index}
                  style={{
                    ...redRow,
                    maxWidth: columnWidth + "px",
                    width: columnWidth + "px",
                    height: blockHeight,
                    display: "flex",
                    alignItems: "center",
                    borderRight: "solid 1px #e9ecef",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s
                    // baseline alignment of inlinified flex children.
                    verticalAlign: "top",
                    // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                    // padding leak (~12px/side on any direct child of a
                    // ".row").
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {/* CM-52604 (QA punto 2 follow-up): force a single flex
                      line — see partialsHeader for why flex-wrap:wrap
                      silently misplaces the last ("Pro.") sub-column. */}
                  <Row className="mx-0" style={{ flexWrap: "nowrap" }}>
                    {partials.map((_, partialIndex) => (
                      // CM-52604 (QA punto 5): raw per-class partial scores,
                      // stacked (no CF-level partial average here — that only
                      // shows when "Por Parciales" is on).
                      <div
                        key={partialIndex}
                        style={{ width: widthParcial + "px" }}
                      >
                        {element.classes.map((singleClass, c_index) => {
                          const classPeriod =
                            (singleClass.periods &&
                              singleClass.periods[p_index]) ||
                            {};
                          const classPartials = classPeriod.partials || [];
                          return (
                            <div
                              key={c_index}
                              style={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {(classPartials[partialIndex] &&
                                classPartials[partialIndex].score) ||
                                "-"}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    {/* CM-52604 (QA punto 5): "Pro." sub-column merged at
                        CF-group level (element.periods[].average) — was
                        previously showing each class's own average here. */}
                    <div
                      style={{
                        width: widthParcial + "px",
                        height: blockHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <b>{cfPeriod.average || "-"}</b>
                    </div>
                  </Row>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            ...redRow,
            width: "100px",
            height: blockHeight,
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
            // backgroundColor: redRow ? "#FFF0F3" : "#ffffff",
          }}
        >
          <b>{element.average}</b>
        </div>
      </Row>
    );
  };

  /**
   * Development-area row with partials + CF-level averages (CM-52583).
   * Gated by the `partial_averages_in_development_areas` feature flag.
   * For each partial it shows: [left] per-class score | [right] CF-level partial
   * average (vertically merged cell). The "Pr." column shows the CF-level period
   * average, also vertically centered.
   * The CF averages are read from the new `element.periods[]` array (parallel to
   * `element.classes`). Tolerant of missing data: shows "-".
   */
  const developmentAreaRowWithCFPartials = (
    element,
    index,
    redRow,
    space,
    numberOfClasses
  ) => {
    const cfPeriods = element.periods || [];
    const totalPartials = element.classes[0].periods.reduce((acc, period) => {
      const partials = period.partials || [];
      return acc + partials.length + 1;
    }, 0);
    const widthAllPartials = totalPartials * 42;
    const countSurplus = totalPartials - element.classes[0].periods.length * 3;
    if (
      countSurplus > 0 &&
      showPartials &&
      widthAllPartials > widthForPeriod * periods.length
    ) {
      space = widthAllPartials;
    }
    const blockHeight = 40 * numberOfClasses + "px";

    return (
      <Row
        id={"rowDevelopmentArea-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "externalShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: blockHeight,
            width: space + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: blockHeight,
            }}
          >
            {element.classes[0].periods.map((period, p_index) => {
              const partials = period.partials || [];
              // CM-52604 (QA punto 4): shared per-period geometry so this CF row
              // lines up exactly with the header and the other rows.
              const columnWidth = cfPeriodColumnWidth(partials.length);
              const widthParcial = cfPartialCellWidth(partials.length);
              const cfPeriod = cfPeriods[p_index] || {};
              const cfPartialsAvg = cfPeriod.partials_average || [];
              return (
                <div
                  key={p_index}
                  style={{
                    ...redRow,
                    maxWidth: columnWidth + "px",
                    // CM-52583 (QA follow-up): explicit width so the cell
                    // doesn't shrink to its content and every row shares the
                    // exact same period column width.
                    width: columnWidth + "px",
                    height: blockHeight,
                    display: "flex",
                    alignItems: "center",
                    borderRight: "solid 1px #e9ecef",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                    // alignment of inlinified flex children — verified live to
                    // be the root cause of the vertical drift QA reported.
                    verticalAlign: "top",
                    // CM-52604 (QA punto 6): Bootstrap's ".row > *" gutter
                    // padding leak (~12px/side on any direct child of a
                    // ".row") — forced flex-shrink on this cell's sub-columns.
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {partials.map((partialItem, partialIndex) => {
                    return (
                      <div
                        key={partialIndex}
                        style={{
                          width: widthParcial + "px",
                          display: "flex",
                          borderRight: "solid 1px #e9ecef",
                        }}
                      >
                        {/* Left: per-class partial score */}
                        <div
                          style={{
                            width: widthParcial / 2 + "px",
                            borderRight: "solid 1px #e9ecef",
                          }}
                        >
                          {element.classes.map((singleClass, c_index) => {
                            const classPeriod =
                              (singleClass.periods &&
                                singleClass.periods[p_index]) ||
                              {};
                            const classPartials = classPeriod.partials || [];
                            return (
                              <div
                                key={c_index}
                                style={{
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {(classPartials[partialIndex] &&
                                  classPartials[partialIndex].score) ||
                                  "-"}
                              </div>
                            );
                          })}
                        </div>
                        {/* Right: CF-level partial average (centered) */}
                        <div
                          style={{
                            width: widthParcial / 2 + "px",
                            height: blockHeight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(cfPartialsAvg[partialIndex] &&
                            cfPartialsAvg[partialIndex].average) ||
                            "-"}
                        </div>
                      </div>
                    );
                  })}
                  {/* "Pr." column: CF-level period average (centered) */}
                  <div
                    style={{
                      width: widthParcial + "px",
                      height: blockHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <b>{cfPeriod.average || "-"}</b>
                  </div>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            ...redRow,
            width: "100px",
            height: blockHeight,
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
          }}
        >
          <b>{element.average}</b>
        </div>
      </Row>
    );
  };

  /**
   * Development-area row WITHOUT partials but with the CF-level period average
   * ("Pro." column), CM-52583. Mirrors the template when "Promediar Campos
   * Formativos" is enabled. Gated by the FF + presence of `element.periods`
   * (provided by the backend). For each period it shows:
   * [left] per-class score | [right] CF-level period average (vertically merged
   * cell). On the right, the CF total (existing).
   */
  const developmentAreaRowWithCFAverage = (
    element,
    index,
    redRow,
    space,
    numberOfClasses
  ) => {
    const cfPeriods = element.periods || [];
    const blockHeight = 40 * numberOfClasses + "px";

    return (
      <Row
        id={"rowDevelopmentArea-" + index}
        key={index}
        style={{ width: "max-content" }}
        className={hasEvaluationCriteria ? "externalShadow" : ""}
        role={hasEvaluationCriteria ? "button" : null}
        onClick={() => {
          hasEvaluationCriteria ? props.setView("breakdown") : null;
        }}
      >
        <div
          className="px-0"
          style={{
            height: blockHeight,
            width: space + "px",
          }}
        >
          <Row
            className="px-0 mx-0"
            style={{
              display: "block ruby",
              height: blockHeight,
            }}
          >
            {element.classes[0].periods.map((period, p_index) => {
              const cfPeriod = cfPeriods[p_index] || {};
              return (
                <div
                  key={p_index}
                  style={{
                    ...redRow,
                    maxWidth: widthForPeriod + "px",
                    height: blockHeight,
                    display: "flex",
                    alignItems: "center",
                    borderRight: "solid 1px #e9ecef",
                    // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
                    // alignment of inlinified flex children.
                    verticalAlign: "top",
                  }}
                >
                  {/* Left: per-class period score */}
                  <div
                    style={{
                      width: widthForPeriod / 2 + "px",
                      borderRight: "solid 1px #e9ecef",
                    }}
                  >
                    {element.classes.map((singleClass, c_index) => {
                      const classPeriod =
                        (singleClass.periods &&
                          singleClass.periods[p_index]) ||
                        {};
                      return (
                        <div
                          key={c_index}
                          style={{
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {classPeriod.score || "-"}
                        </div>
                      );
                    })}
                  </div>
                  {/* Right: CF-level period average (centered) */}
                  <div
                    style={{
                      width: widthForPeriod / 2 + "px",
                      height: blockHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <b>{cfPeriod.average || "-"}</b>
                  </div>
                </div>
              );
            })}
          </Row>
        </div>
        <div
          style={{
            ...redRow,
            width: "100px",
            height: blockHeight,
            display: "flex",
            alignItems: "center",
            border: "solid 1px #e9ecef",
          }}
        >
          <b>{element.average}</b>
        </div>
      </Row>
    );
  };

  /**
   * Blank divider row
   * @param {*} index
   * @returns
   */
  const blankDividerRow = (index) => {
    return (
      <Row
        key={index}
        style={{
          display: "block ruby",
          // CM-52589 (CF flow): must match the left ("classes") column divider
          // paddingTop (20px) used for the "average"/"development_area_average"
          // rows; otherwise the two columns end up with different total heights
          // and drift apart at the bottom when scrolled all the way down.
          // CM-52583 (QA follow-up): "block ruby" adds ~5px of inline line-box
          // below the inner div, so paddingTop(20) + inner(20) + 5px = 45px,
          // while the classes-column divider (plain divs, no ruby) is exactly
          // 40px. That 5px pushes the AVERAGE value row down relative to its
          // label, so the height is forced to 40px (clipping the ruby line-box).
          // Both adjustments are CF-only: master's 15 + 20 + 5 = 40px balance is
          // what the legacy flow must keep.
          paddingTop: cfFeatureActive ? "20px" : "15px",
          width: "max-content",
          ...(cfFeatureActive ? { height: "40px", overflow: "hidden" } : {}),
        }}
      >
        <div
          style={{
            //  ...customStyles.fillCaracter,
            width: widthForPeriod * periods.length + 100 + "px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            paddingLeft: 0,
            // CM-52604 (QA punto 4): neutralize "block ruby"'s baseline
            // alignment of inlinified flex children (see headerPeriods).
            ...(cfFeatureActive ? { verticalAlign: "top" } : {}),
          }}
        ></div>
      </Row>
    );
  };

  // const dottedDividerRow = (element, index) => {
  //   const totalPartials = element.periods.reduce((acc, period) => {
  //     const partials = period.partials || [];
  //     return acc + partials.length;
  //   }, 0);
  //   const countSurplus = totalPartials - element.periods.length * 3;
  //   let rowWidth = widthForPeriod * periods.length;
  //   if (countSurplus > 0 && showPartials) {
  //     rowWidth = rowWidth + countSurplus * 42;
  //   }
  //   return (
  //     <Row
  //       key={index}
  //       style={{
  //         display: "block ruby",
  //         width: "max-content",
  //         paddingTop: "20px",
  //         height: "40px",
  //       }}
  //     >
  //       <div
  //         style={{
  //           //  ...customStyles.fillCaracter,
  //           width: rowWidth + 100 + "px",
  //           height: "39.5px",
  //           display: "flex",
  //           alignItems: "center",
  //           paddingLeft: 0,
  //           borderTop: "dashed 1px black",
  //         }}
  //       ></div>
  //     </Row>
  //   );
  // };

  /**
   * Legacy element dispatch — byte-identical to master's, used whenever the
   * `partial_averages_in_development_areas` FF is off (CM-52583, QA follow-up).
   * Every renderer it calls is also kept as master left it, so with the FF off
   * the report card renders exactly as in production. `development_area_average`
   * elements fall through and render nothing, as in master.
   * @param {Object} element
   * @param {number} index
   * @param {Object} redRow
   * @returns {JSX.Element|undefined}
   */
  const legacyElementRow = (element, index, redRow) => {
    // Development-area (CF) element
    if (element.type == "development_area") {
      const numerOfperiods = element.classes[0].periods.length;
      const numberOfClasses = element.classes.length;
      const space = widthForPeriod * numerOfperiods;
      return showPartials && isFeatureEnabled("Mostrar_Parciales_en_Boletas")
        ? developmentAreaRowWithPartials(
            element,
            index,
            redRow,
            space,
            numberOfClasses
          )
        : developmentAreaRow(element, index, redRow, space, numberOfClasses);
    }
    // Class element
    if (element.type == "class") {
      const numerOfperiods = element.periods.length;
      const space = widthForPeriod * numerOfperiods;
      return showPartials && isFeatureEnabled("Mostrar_Parciales_en_Boletas")
        ? classRowWithPartials(element, index, redRow, space)
        : classRow(element, index, redRow, space);
    }
    // Average element
    if (element.type == "average") {
      return (
        <>
          {blankDividerRow(index)}
          {showPartials && isFeatureEnabled("Mostrar_Parciales_en_Boletas")
            ? averageRowWhithPartials(element, index, redRow)
            : averageRow(element, index, redRow)}
        </>
      );
    }
  };

  if (loading) {
    return (
      <Row className="justify-content-md-center mt-5">
        <Col xxl={7} xl={7} lg={12} xs={12} style={customStyles.reportCardBase}>
          <div>
            <Row className="table-lazyLoading mt-4">
              <Col md={7} xxl={7} xl={7} lg={7} xs={12}>
                <div
                  style={{
                    width: "90px",
                    height: "90px",
                    display: "inline-block",
                  }}
                ></div>
                <div
                  style={{
                    width: "200px",
                    height: "20px",
                    display: "inline-block",
                    bottom: "69px",
                    marginLeft: "20px",
                  }}
                ></div>
              </Col>
              <Col md={5} xxl={5} xl={5} lg={5} xs={12}>
                <div
                  style={{
                    width: "200px",
                    height: "20px",
                    marginLeft: "20px",
                  }}
                ></div>
                <div
                  style={{
                    width: "200px",
                    height: "20px",
                    marginLeft: "20px",
                  }}
                ></div>
                <div
                  style={{
                    width: "200px",
                    height: "20px",
                    marginLeft: "20px",
                  }}
                ></div>
              </Col>
            </Row>
            <Row className="table-lazyLoading mt-4">
              <Col md={12}>
                <div style={customStyles.lazy}></div>
                <div style={customStyles.lazy} className="mt-4"></div>
                <div style={customStyles.lazy} className="mt-4"></div>
                <div style={customStyles.lazy} className="mt-4"></div>
                <div style={customStyles.lazy} className="mt-4"></div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    );
  }

  if (!loading) {
    return (
      <Row className="justify-content-md-center mt-5">
        <Col xxl={8} xl={8} lg={12} xs={12} style={customStyles.reportCardBase}>
          {/* Head ReportCard */}
          <Row className="mb-4">
            <Col md={8}>
              <Row>
                <div style={{ width: "max-content", display: "inline-block" }}>
                  <img
                    src={reportCardInfo.organization_logo_url}
                    alt="logo"
                    style={{ maxWidth: "90px" }}
                  />
                </div>
                <div style={{ width: "max-content", display: "inline-block" }}>
                  <Row>
                    <Col md={12}>
                      <h6>
                        <b>{reportCardInfo.organization_name}</b>
                      </h6>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>{reportCardInfo.student_name}</Col>
                  </Row>
                </div>
              </Row>
            </Col>
            <Col md={4}>
              <Row>
                <Col md={12}>
                  <b>{t("reportCards:reportCardBasic.schoolCycle") + " :"} </b>
                  {reportCardInfo.school_cycle_name}
                </Col>
                <Col md={12}>
                  <b>{t("reportCards:reportCardBasic.schooling") + " :"} </b>
                  {reportCardInfo.school_level_name}
                </Col>
                <Col md={12}>
                  <b>{t("reportCards:reportCardBasic.grade") + " :"} </b>
                  {reportCardInfo.grade_level}{" "}
                  <b>{t("reportCards:reportCardBasic.group") + " :"} </b>
                  {reportCardInfo.grade_group}
                </Col>
              </Row>
            </Col>
          </Row>
          {/* Table info */}
          {/* CM-52583 (QA follow-up): the single-scrollbar layout (scroll owned
              by the periods column and mirrored onto the classes column) is
              CF-only. With the FF off the scroll stays on this Row, as in
              master. */}
          <Row
            style={{
              ...dinamicHeightReport,
              ...(cfFeatureActive ? { overflowY: "hidden" } : {}),
            }}
          >
            {/* Columna de lateral izquierda (classes, develpment area, average) */}
            <div
              ref={cfFeatureActive ? classesColumnRef : null}
              onWheel={cfFeatureActive ? handleClassesWheel : undefined}
              style={{
                ...customStyles.columnClasses,
                ...(cfFeatureActive
                  ? {
                      ...customStyles.columnClassesCF,
                      height: classesViewportHeight,
                    }
                  : {}),
              }}
            >
              {hasDevelopmentAreas ? (
                <div
                  className="text-center"
                  style={showPartials ? { height: "36px" } : {}}
                >
                  <b>{t("reportCards:reportCardBasic.developmentAreas")}</b>
                </div>
              ) : (
                <b
                  style={{
                    height: showPartials ? "36px" : "18px",
                    display: "block",
                  }}
                >
                  {" "}
                </b>
              )}
              {reportCardInfo.elements.map((element, index) => {
                return (
                  <Row key={index}>
                    {element.type == "class" && (
                      <>
                        {hasDevelopmentAreas && (
                          <Col
                            md={3}
                            style={{
                              ...customStyles.verticalText,
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          ></Col>
                        )}
                        <Col md={hasDevelopmentAreas ? 9 : 12}>
                          <Row>
                            <div
                              className="align-middle"
                              style={{
                                border: "solid 1px #e9ecef",
                                height: "40px",
                                //  display: "flex",
                                // alignItems: "center",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                paddingTop: "10px",
                              }}
                            >
                              {element.name}
                            </div>
                          </Row>
                        </Col>
                      </>
                    )}
                    {element.type == "development_area" && (
                      <>
                        <Col
                          md={3}
                          sm={3}
                          xs={3}
                          style={{
                            ...customStyles.verticalText,
                            maxHeight: 40 * element.classes.length + "px",
                            "-webkit-box-orient": "vertical",
                            overflow: "hidden",
                            "text-overflow": "ellipsis",
                            paddingLeft: 0,
                            paddingRight: "10px",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          {element.name}
                        </Col>
                        <Col md={9} sm={9} xs={9}>
                          {element.classes.map((singleClass, c_index) => {
                            /* CM-52589: per-class weight shown right-aligned in
                               the same cell (matches the Figma column), as a
                               plain "NN%". Gated by the FF + switch
                               (showCFWeights); the type/range check is defensive
                               so an unexpected backend value (or a
                               differently-named field) fails safe by simply not
                               rendering, instead of showing "NaN%"/"undefined%"
                               or an out-of-range value. */
                            const weight = singleClass.cf_weight_percentage;
                            const renderWeight =
                              showCFWeights &&
                              typeof weight === "number" &&
                              weight >= 0 &&
                              weight <= 100;
                            return (
                              <Row key={c_index}>
                                <div
                                  className="align-middle text-break"
                                  style={{
                                    border: "solid 1px #e9ecef",
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    // CM-52583 (QA follow-up): the two-slot
                                    // layout only when the weight is really
                                    // rendered; otherwise this cell keeps
                                    // master's single-child markup.
                                    ...(renderWeight
                                      ? { justifyContent: "space-between" }
                                      : {}),
                                  }}
                                >
                                  {renderWeight ? (
                                    <>
                                      <span
                                        style={{
                                          minWidth: 0,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {singleClass.name}
                                      </span>
                                      {/* text-nowrap + flex-shrink-0 keep it from
                                          breaking or being clipped on small
                                          screens. */}
                                      <span
                                        id={
                                          "classWeightValue-boleta-" +
                                          index +
                                          "-" +
                                          c_index
                                        }
                                        className="text-nowrap flex-shrink-0 ms-2"
                                      >
                                        {weight + "%"}
                                      </span>
                                    </>
                                  ) : (
                                    singleClass.name
                                  )}
                                </div>
                              </Row>
                            );
                          })}
                        </Col>
                      </>
                    )}
                    {element.type == "average" && (
                      <>
                        <div className="px-0" style={{ paddingTop: "20px" }}>
                          <div
                            style={{
                              //  ...customStyles.fillCaracter,
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              overflow: "hidden",
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            border: "solid 1px #e9ecef",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            // CM-52583: separates the label from the info icon
                            // below, which only renders in CF mode.
                            ...(cfMode ? { gap: "6px" } : {}),
                          }}
                        >
                          <b>{t("reportCards:reportCardBasic.average")}</b>
                          {/* CM-52583: clarifies that this row averages each
                              subject on its own, not the CF "Pro." columns
                              above — prevents the same doubt QA raised. Only
                              relevant when CFs are actually active (cfMode)
                              AND present in this report card
                              (hasDevelopmentAreas) — no "Pro." column exists
                              to clarify otherwise. Legacy flow keeps no icon. */}
                          {cfMode && hasDevelopmentAreas && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="averageRowClarification">
                                  {t("reportCards:reportCardBasic.averageTooltip")}
                                </Tooltip>
                              }
                            >
                              <i
                                className="fas fa-info-circle"
                                style={{ color: "#ea2c54" }}
                              />
                            </OverlayTrigger>
                          )}
                        </div>
                      </>
                    )}
                    {element.type == "development_area_average" &&
                      showCFPeriodAvg && (
                        <>
                          <div className="px-0" style={{ paddingTop: "20px" }}>
                            <div
                              style={{
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                              }}
                            ></div>
                          </div>
                          <div
                            style={{
                              border: "solid 1px #e9ecef",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: "#FFF0F3",
                            }}
                          >
                            <b>{t("reportCards:reportCardBasic.average")}</b>
                          </div>
                        </>
                      )}
                  </Row>
                );
              })}
            </div>

            {/* Periodos y calificaciones */}
            <div
              ref={cfFeatureActive ? periodsColumnRef : null}
              onScroll={cfFeatureActive ? handleVerticalScrollSync : undefined}
              style={{
                ...customStyles.columnPeriods,
                ...(cfFeatureActive
                  ? {
                      ...customStyles.columnPeriodsCF,
                      height: dinamicHeightReport.height,
                    }
                  : {}),
              }}
              id="containerPeriods"
            >
              {/* Header periodos */}
              {headerPeriods()}
              {/* Header partials. CM-52583 (QA obs. 1): in CF mode with no
                  partials created, the standard partials header renders a
                  single full-width "Pro." cell that drifts from the split
                  body cells — use the CF sub-header (½ empty | ½ "Pro.")
                  instead. Legacy flow (FF off) untouched: it keeps master's
                  own partialsHeader(). */}
              {/* CM-52604 (QA punto 4): the CF sub-header only applies when the
                  report card is actually grouped by CF; a flat response (no
                  development_area) uses the standard partials header even with
                  the CF flags on. */}
              {!cfFeatureActive ? (
                showPartials ? (
                  partialsHeader()
                ) : null
              ) : (
                <>
                  {showPartials &&
                  (!showCFPartialAvg ||
                    dataHasPartials ||
                    !hasDevelopmentAreas)
                    ? cfPartialsHeader()
                    : null}
                  {showPartials &&
                  showCFPartialAvg &&
                  !dataHasPartials &&
                  hasDevelopmentAreas
                    ? cfAverageSubHeader()
                    : null}
                </>
              )}
              {/* Iterar elementos de boleta */}
              {reportCardInfo.elements.map((element, index) => {
                const isEven = (num) => {
                  return num % 2 === 0;
                };

                const redRow = isEven(index + 1)
                  ? customStyles.columnScoreRed
                  : customStyles.columnScore;
                // CM-52583 (QA follow-up): FF off → legacy dispatch, identical
                // to master. Everything below this line belongs to the CF flow.
                if (!cfFeatureActive) {
                  return legacyElementRow(element, index, redRow);
                }
                // Development-area (CF) element
                if (element.type == "development_area") {
                  const numerOfperiods = element.classes[0].periods.length;
                  const numberOfClasses = element.classes.length;
                  const space = widthForPeriod * numerOfperiods;
                  // CM-52583: the CF-level average columns are controlled by the
                  // endpoint flag `show_cf_partial_avg` (same as the template),
                  // already combined with the FF in `showCFPartialAvg`.
                  // QA obs. 1: with no partials created (dataHasPartials =
                  // false) fall through to the "period average only" renderer
                  // so body geometry matches the CF sub-header.
                  if (showCFPartialAvg && showPartials && dataHasPartials) {
                    return developmentAreaRowWithCFPartials(
                      element,
                      index,
                      redRow,
                      space,
                      numberOfClasses
                    );
                  }
                  // CM-52604 (QA punto 2/3 follow-up): the per-period CF
                  // average (`element.periods[].average`) is returned by the
                  // endpoint whenever the classes are grouped by CF, regardless
                  // of "Por Parciales" (`showCFPartialAvg`) — it was only ever
                  // rendered when that switch was also on, so the column was
                  // silently dropped with just "Promediar Campos Formativos"
                  // active. Render it whenever we're not showing the per-class
                  // partial breakdown (that combination — partials shown
                  // without "Por Parciales" — is tracked separately).
                  if (
                    showCFPartialAvg ||
                    !showPartials ||
                    !isFeatureEnabled("Mostrar_Parciales_en_Boletas")
                  ) {
                    return developmentAreaRowWithCFAverage(
                      element,
                      index,
                      redRow,
                      space,
                      numberOfClasses
                    );
                  }
                  return developmentAreaRowWithCFAvgAndPartials(
                    element,
                    index,
                    redRow,
                    space,
                    numberOfClasses
                  );
                }
                // Class element
                if (element.type == "class") {
                  const numerOfperiods = element.periods.length;
                  const space = widthForPeriod * numerOfperiods;
                  return showPartials &&
                    isFeatureEnabled("Mostrar_Parciales_en_Boletas")
                    ? cfClassRowWithPartials(element, index, redRow, space)
                    : classRow(element, index, redRow, space);
                }
                // Average element
                if (element.type == "average") {
                  return (
                    <>
                      {blankDividerRow(index)}
                      {showPartials &&
                      isFeatureEnabled("Mostrar_Parciales_en_Boletas")
                        ? cfAverageRowWithPartials(element, index, redRow)
                        : averageRow(element, index, redRow)}
                    </>
                  );
                }
                // CM-52586: cross-CF average row — gated by show_cf_period_avg.
                // Invisible when the flag is off; no fallback renderer needed.
                if (element.type == "development_area_average") {
                  if (!showCFPeriodAvg) return null;
                  return (
                    <>
                      {blankDividerRow(index)}
                      {/* QA obs. 1 (follow-up): the "AndPartials" variant only
                          when the body rows really render partials (partials
                          switch ON and data has partials), so the AVERAGE row
                          always mirrors the CF rows' geometry. */}
                      {showCFPartialAvg && showPartials && dataHasPartials
                        ? averageRowWithCFPeriodAvgAndPartials(element, index)
                        : averageRowWithCFPeriodAvgOnly(element, index)}
                    </>
                  );
                }
              })}
            </div>
          </Row>
        </Col>
      </Row>
    );
  }
};

export default ReportCardComponent;


ReportCardComponent.propTypes = {
  setStudentName: PropTypes.func,
  setView: PropTypes.func,
};
